import type { FieldReadFunction, Reference } from '@apollo/client'

import { getPredictedRevenueFromClient } from './getPredictedRevenueFromClient'
import { getPredictedRevenueFromGI } from './getPredictedRevenueFromGI/getPredictedRevenueFromGI'
import { getPredictedRevenueFromMortgage } from './getPredictedRevenueFromMortgage'
import { getPredictedRevenueFromProtection } from './getPredictedRevenueFromProtection'

const read: FieldReadFunction<PredictedRevenueEntry[]> = (_, { readField, variables, toReference }) => {
  const caseId = variables!.caseId
  const caseRef = toReference(`Case:${caseId}`)
  const mortgagesRef = readField<undefined | Reference[]>('mortgages', caseRef)
  const preferenceLoanAmount = readField<Maybe<string> | undefined>('preference_loan_amount', caseRef)
  const mortgageReason = readField<Maybe<MortgageReason>>('preference_mortgage_reason', caseRef)

  const mortgages = (mortgagesRef || []).map((mortgageRef) => {
    const mortgageProductRef = readField<MortgageProduct>('mortgage_product', mortgageRef)
    const lenderName = readField<string>('lender_name', mortgageProductRef)
    return {
      case_ids: readField<Maybe<string>[]>('case_ids', mortgageRef) as Maybe<string>[],
      status: readField<MortgageStatus>('status', mortgageRef),
      calculated_values: readField<MortgageCalculatedValues>('calculated_values', mortgageRef),
      mortgage_amount: readField<string>('mortgage_amount', mortgageRef),
      mortgage_product: {
        lender_name: lenderName,
      },
    }
  })

  // Getting all case fees
  const newFees = readField<NewFee[]>('fees', caseRef)?.map((feeRef) => ({
    fee_type: readField('fee_type', feeRef) as FeeType,
    value: readField('value', feeRef) as string,
  }))

  // Getting all protection products
  const protectionProductsRef = readField<undefined | Reference[]>({
    fieldName: 'protectionProducts',
    args: {
      protectionProductDetails: true,
      filterCaseIds: [variables!.caseId],
    },
  })

  // Getting all GI products
  const giProductsResponse = readField<GeneralInsuranceProductsResponse | undefined>({
    fieldName: 'generalInsuranceProducts',
    args: {
      input: { filter_case_ids: [variables!.caseId] },
    },
  })

  // Cases should include additional borrowing to fees
  const additionalLoans = readField<undefined | AdditionalLoans[]>('additional_loans', caseRef)?.map(
    (additionalLoansRef) => ({
      loan_amount: readField('loan_amount', additionalLoansRef) as string,
      loan_purpose: readField('loan_purpose', additionalLoansRef) as LoanType,
    }),
  )

  const protectionProducts = protectionProductsRef?.map((ref) => {
    return readField('details', ref) as ProtectionProductDetails
  })

  const giProducts = readField<CreateGeneralInsuranceProduct[] | undefined>(
    'general_insurance_products',
    giProductsResponse || {},
  )?.map((ref) => {
    return readField('details', ref) as CreateGeneralInsuranceProductDetails
  })

  const res = calculatePredictedRevenue({
    caseId,
    mortgageReason,
    mortgages,
    protectionProducts,
    newFees,
    preferenceLoanAmount,
    additionalLoans,
    giProducts,
  })

  return res
}

const mortgageCaseTypes: MortgageReason[] = [
  'REASON_FTB',
  'REASON_HOUSE_MOVE',
  'REASON_REMORTGAGE',
  'REASON_BTL',
  'REASON_BTL_REMORTGAGE',
  'REASON_EQUITY_RELEASE',
  'REASON_FURTHER_ADVANCE',
  'REASON_COMMERCIAL',
  'REASON_BRIDGING',
]

type Props = {
  caseId: string
  mortgageReason: Maybe<MortgageReason>
  newFees?: NewFee[]
  mortgages?: Partial<Mortgage>[]
  protectionProducts?: ProtectionProductDetails[]
  preferenceLoanAmount?: Maybe<string>
  additionalLoans?: AdditionalLoans[]
  giProducts?: CreateGeneralInsuranceProductDetails[]
}

export const calculatePredictedRevenue = ({
  caseId,
  mortgageReason,
  mortgages,
  protectionProducts,
  newFees,
  preferenceLoanAmount,
  additionalLoans,
  giProducts,
}: Props): PredictedRevenueEntry[] => {
  const res = [
    ...getPredictedRevenueFromGI(giProducts || []),
    ...getPredictedRevenueFromProtection(protectionProducts || []),
  ] as PredictedRevenueEntry[]

  if (mortgageReason && mortgageCaseTypes.includes(mortgageReason)) {
    res.push(...getPredictedRevenueFromClient(newFees, mortgages, preferenceLoanAmount, additionalLoans))
  }

  const revenueFromMortgageProducts = getPredictedRevenueFromMortgage(caseId, mortgages)
  if (revenueFromMortgageProducts) {
    res.push(revenueFromMortgageProducts)
  }

  return res
}

export default read
